import React, { useState, useEffect } from 'react'
import { Row, Col, Container } from 'react-bootstrap'
import { useHistory, useLocation } from 'react-router-dom'
import { ShimmerThumbnail, ShimmerTitle } from 'react-shimmer-effects'
import { Helmet } from 'react-helmet'
import { useUserService, useNotificationService } from '@abroad/components'
import { PlanPaymentStepper } from '../PlanAndPayment'
import { CustomShimmerPayment, PageHeader } from '../common'
import { useParams } from 'react-router'
import { QuestPaymentMethods, QuestAmountInfo, QuestUserInfoForm } from './'
import API from '../../utils/API'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import CheckoutForm from './CheckoutForm'
import QuestPaymentStatus from './QuestPaymentStatus'
import TransferInstructions from './TransferInstructions'
import { gtmViewExperiencePaymentAction } from './gtmfunctions'
import {
  gtmContinueExperiencePaymentMethodAction,
  gtmContinueExperienceBillingInfoAction,
} from './gtmfunctions'
import moment from 'moment-timezone'

const DEPOSIT_CUTOFF_DAYS = 120

/** Use only programDatesList[0].startDate for the deposit cutoff. */
const getFirstProgramStartDate = (quest) =>
  quest?.programDatesList?.[0]?.startDate || null

const getRemainingDaysUntilStart = (startDate) => {
  if (!startDate) return null

  return moment
    .utc(startDate)
    .startOf('day')
    .diff(moment.utc().startOf('day'), 'days')
}

// Hide deposit when remaining days until start date is < 120
const isWithinDepositCutoff = (remainingDays) =>
  remainingDays !== null && remainingDays < DEPOSIT_CUTOFF_DAYS

const defaultAddress = {
  billerName: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)

const QuestPayment = () => {
  const { user } = useUserService()
  const userId = user?._id
  const { questId } = useParams()
  const location = useLocation()
  const query = new URLSearchParams(location?.search)
  const stepNo = query.get('step')
  const payment_intent_client_secret = query.get('payment_intent_client_secret')
  const redirect_status = query.get('redirect_status')
  const [paymentSummary, setPaymentSummary] = useState(null)
  const [quest, setQuest] = useState(null)
  const totalAmount = paymentSummary?.totalCost || 0
  const actualCost = paymentSummary?.actualCost || 0
  const showDiscount =
    typeof paymentSummary?.discountedCost === 'number' &&
    paymentSummary.discountedCost > 0
  const discountCost =
    showDiscount && paymentSummary?.actualCost - paymentSummary?.discountedCost
  const depositAmount = paymentSummary?.depositAmount || 0
  const [finalAmountToPay, setFinalAmountToPay] = useState(
    Number(depositAmount) || 0,
  )
  const [paymentType, setPaymentType] = useState('deposit')
  const [step, setStep] = useState(Number(stepNo) || 1)
  const [selectedMethod, setSelectedMethod] = useState()
  const [billingAddress, setBillingAddress] = useState(defaultAddress)
  const [showAddressForm, setShowAddressForm] = useState(false)
  const history = useHistory()
  const [supportedPaymentMethods, setSupportedPaymentMethods] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const notification = useNotificationService()
  const [fundingInstructions, setFundingInstructions] = useState(null)
  const [clientSecret, setClientSecret] = useState(
    payment_intent_client_secret || null,
  )
  const [eurRate, setEurRate] = useState(null)
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(false)
  const [originalFlowType, setOriginalFlowType] = useState(null)

  const firstProgramStartDate = getFirstProgramStartDate(quest)
  const remainingDays = getRemainingDaysUntilStart(firstProgramStartDate)
  // Show deposit only when deposit > 0 AND remaining days until start is NOT < 120
  const showDepositOption =
    depositAmount > 0 && !isWithinDepositCutoff(remainingDays)
  const displayDepositAmount = showDepositOption ? depositAmount : 0
  const baseAmount =
    paymentType === 'deposit' && showDepositOption
      ? Number(depositAmount)
      : Number(totalAmount)

  useEffect(() => {
    if (questId) {
      // GTM tracking for experience payment view
      gtmViewExperiencePaymentAction(questId, 'Experience Payment', userId)
    }
  }, [questId])

  // Fetch user address on mount
  useEffect(() => {
    const getUserAddress = async () => {
      try {
        const { data } = await API.user.getUserAddress(userId)
        if (data?.address) {
          Object.keys(data?.address).forEach((key) => {
            if (data?.address[key] === null || data?.address[key] === '') {
              delete data?.address[key]
            }
          })
          const updatedAddress = {
            ...data?.address,
            billerName: data?.name || '',
          }
          setBillingAddress((prev) => ({
            ...prev,
            ...updatedAddress,
          }))
        }
      } catch (e) {
        notification.showNotification(
          'Could not fetch billing address.',
          'danger',
        )
      } finally {
        // setIsAddressLoading(false) // This line was removed
      }
    }
    if (userId) getUserAddress()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  // Helper: check if all address fields are empty
  const isAddressEmpty = Object.values(billingAddress).every((v) => !v)

  // Determine step count and labels
  // Previous ACH logic used Stripe ACH checkout (confirmUsBankAccountPayment).
  // ACH now uses the wire transfer bank-instructions popup instead.
  // const isWireTransfer = selectedMethod === 'wire_transfer'
  const isAchMethod = selectedMethod === 'ach'
  const usesWireTransferPopup =
    selectedMethod === 'wire_transfer' || isAchMethod
  // API expects wire_transfer to return fundingInstructions for the popup.
  // Previous ACH API value:
  // const apiPaymentMethod = selectedMethod
  const apiPaymentMethod = isAchMethod ? 'wire_transfer' : selectedMethod
  const isWireTransfer = usesWireTransferPopup

  // Determine if we should use 3 steps based on original flow or current state
  const shouldUseThreeSteps = () => {
    // If we have a stored original flow type, use it
    if (originalFlowType) {
      return originalFlowType === '3-step'
    }
    // Otherwise, calculate based on current state
    return !isWireTransfer && isAddressEmpty
  }

  const useThreeSteps = shouldUseThreeSteps()
  const stepCount = isWireTransfer ? 2 : useThreeSteps ? 3 : 2

  // Reset original flow type when user goes back to step 1
  useEffect(() => {
    if (step === 1) {
      setOriginalFlowType(null)
    }
  }, [step])

  useEffect(() => {
    const fetchExchangeRate = async () => {
      if (selectedMethod === 'sepa') {
        try {
          const { data } = await API.payment.getCurrencyExchangeRate(
            'usd',
            'eur',
          )
          const eur = data.find((d) => d.currency === 'EUR')
          if (eur) {
            setEurRate(eur.rate)
          }
        } catch (error) {
          notification.showNotification(
            'Could not fetch exchange rate.',
            'danger',
          )
        }
      } else {
        setEurRate(null)
      }
    }
    fetchExchangeRate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calculateFinalAmount = () => {
    const amountToPay = paymentSummary?.canPayRemaining
      ? paymentSummary?.remainingBalance
      : baseAmount
    return Number(amountToPay) || 0
  }

  useEffect(() => {
    const fetchSecondStepEnrollmentProcess = async () => {
      try {
        setIsLoading(true)
        const { data } = await API.quests.getQuestSecondStepEnrollmentProcess(
          questId,
          userId,
        )
        if (data?.paymentSummary?.remainingBalance === 0) {
          history.goBack()
        }

        // Payments endpoint omits dates; use public quest programDatesList[0] only.
        let questData = data?.quest || {}
        try {
          const { data: publicQuest } = await API.quests.getProspetityQuest(
            questId,
          )
          questData = {
            ...questData,
            programDatesList: publicQuest?.programDatesList || [],
          }
        } catch (dateError) {
          // Continue without dates — deposit option stays available
        }

        const firstStartDate = getFirstProgramStartDate(questData)
        const daysUntilStart = getRemainingDaysUntilStart(firstStartDate)
        const hideDeposit = isWithinDepositCutoff(daysUntilStart)
        const depositAmountFromApi = data?.paymentSummary?.depositAmount || 0
        if (
          hideDeposit ||
          (depositAmountFromApi === 0 && data?.paymentSummary?.canPayFull)
        ) {
          setPaymentType('full')
        } else if (depositAmountFromApi > 0) {
          // Default to deposit when deposit option is available
          setPaymentType('deposit')
        }
        setQuest(questData)
        setPaymentSummary(data?.paymentSummary)
        setSupportedPaymentMethods(data?.supportedPaymentMethods)
        setIsLoading(false)
      } catch (error) {
        notification.showNotification(
          'Something went wrong! Please try again later.',
          'danger',
        )
        history.goBack()
      }
    }
    fetchSecondStepEnrollmentProcess()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, userId])

  useEffect(() => {
    const val = calculateFinalAmount()
    setFinalAmountToPay(val)
  }, [paymentType, depositAmount, totalAmount, calculateFinalAmount])

  const renderPageHelmet = () => {
    return (
      <Helmet>
        <title>{'Quest Payment | Abroad'}</title>
        <meta name='title' content={'Quest Payment | Abroad'}></meta>
        <meta property='og:title' content={'Quest Payment | Abroad'}></meta>
        <meta
          property='twitter:title'
          content={'Quest Payment | Abroad'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/quests`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/quests`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/quests`}></meta>
      </Helmet>
    )
  }

  const renderPageHeader = () => {
    return (
      <PageHeader
        showBackArrow={true}
        version='small'
        title='Your Experiences'
        backArrowAction={() =>
          history.push(
            questId
              ? `/your-experiences?questId=${questId}`
              : '/your-experiences',
          )
        }
      />
    )
  }

  const makePayment = (data, address) => {
    if (address) {
      setBillingAddress(address)
    }
    if (isWireTransfer) {
      // ACH and wire_transfer: show bank instructions popup
      setFundingInstructions({
        ...data?.fundingInstructions,
        amount: data?.amount,
      })
    } else {
      // Previous ACH path (Stripe client secret → CheckoutForm):
      // if (selectedMethod === 'ach') {
      //   setClientSecret(data?.stripe?.clientSecret)
      //   setStep(3)
      // }
      setClientSecret(data?.stripe?.clientSecret)
      setStep(3)
    }
  }

  // Extracted payment intent creation logic
  const createPaymentIntent = async (address = billingAddress) => {
    try {
      const requestBody = {
        paymentType: paymentSummary?.canPayRemaining
          ? 'remaining'
          : showDepositOption && paymentType === 'deposit'
          ? 'deposit'
          : 'full',
        // Previous ACH: paymentMethod: selectedMethod ('ach')
        paymentMethod: apiPaymentMethod,
        billerName: address.billerName,
        address: {
          line1: address.line1,
          line2: address.line2,
          state: address.state,
          country: address.country,
          city: address.city,
          postal_code: address.postal_code,
        },
      }
      const { data } = await API.quests.makePayment(questId, requestBody)
      if (isWireTransfer) {
        setFundingInstructions({
          ...data?.fundingInstructions,
          amount: data?.amount,
        })
      } else {
        // Previous ACH path created a Stripe client secret for ACH Payment Element
        setClientSecret(data?.stripe?.clientSecret)
      }
      return data
    } catch (error) {
      if (error?.code === 'amount_too_large') {
        notification.showNotification(
          'SEPA payment limit is €10,000. Please use a different method',
          'danger',
        )
        return
      }

      notification.showNotification(
        'Something went wrong! Please try again.',
        'danger',
      )
      return null
    }
  }

  // Handler for QuestPaymentMethods continue in 2-step flow
  const handlePaymentMethodContinue = async () => {
    gtmContinueExperiencePaymentMethodAction(questId, selectedMethod, userId)
    gtmContinueExperiencePaymentMethodAction(questId, selectedMethod, userId)
    setIsCreatingPaymentIntent(true)

    // Determine and lock in the flow type based on current state when continuing from step 1
    const flowType = !isWireTransfer && isAddressEmpty ? '3-step' : '2-step'
    setOriginalFlowType(flowType)

    if (flowType === '2-step' && !isWireTransfer) {
      // 2-step flow: create payment intent and then advance
      const data = await createPaymentIntent(billingAddress)
      if (data && data.stripe && data.stripe.clientSecret) {
        setStep(2)
      }
      setIsCreatingPaymentIntent(false)
    } else {
      // 3-step or wire transfer: just advance
      setStep(2)
      setIsCreatingPaymentIntent(false)
    }
  }

  return (
    <>
      {renderPageHelmet()}
      <main>
        {renderPageHeader()}
        <section className='mt-52px'>
          <Container>
            <Row className='d-flex justify-content-center p-2'>
              {/* Left: Amount Info */}
              {isLoading ? (
                <div>
                  <Row className='d-flex justify-content-center'>
                    <Col
                      xl={5}
                      lg={6}
                      md={8}
                      sm={10}
                      xs={12}
                      className='mt-2 me-lg-5'>
                      <CustomShimmerPayment />
                    </Col>
                    <Col xl={1}></Col>
                    <Col
                      xl={5}
                      lg={6}
                      md={8}
                      sm={10}
                      xs={12}
                      className='px-0 pt-lg-2 pt-md-4 pt-sm-4 pt-4'>
                      <div className='cursor-pointer border-radius-5'>
                        <ShimmerThumbnail height={100} className='' />
                        <Row className='mt-2'>
                          {Array.from({ length: 5 }).map((_, index) => (
                            <Row key={index} className='mt-1'>
                              <Col sm={2} key={index}>
                                <ShimmerTitle line={1} />
                              </Col>
                              <Col sm={8}>
                                <ShimmerTitle line={1} />
                              </Col>
                            </Row>
                          ))}
                          <Col sm={12} md={6} lg={12} className='mt-2'>
                            <ShimmerThumbnail height={70} />
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </div>
              ) : (
                <>
                  <Col className='px-0' xl={5} lg={6} md={8} sm={10} xs={12}>
                    <h1 className='s1 tracking-normal mb-3'>{quest?.title}</h1>
                    <QuestAmountInfo
                      disabled={step !== 1}
                      actualCost={actualCost}
                      discountCost={discountCost}
                      showDiscount={showDiscount}
                      discountReason={paymentSummary?.discountReason}
                      deposit={displayDepositAmount}
                      total={totalAmount}
                      selected={paymentType}
                      onSelectionChange={setPaymentType}
                      canPayFull={paymentSummary?.canPayFull}
                      // discount={discount}
                      quest={quest}
                      canPayRemaining={paymentSummary?.canPayRemaining}
                      totalPaid={paymentSummary?.totalPaid}
                      totalCost={paymentSummary?.totalCost}
                      remainingBalance={paymentSummary?.remainingBalance}
                      eurRate={eurRate}
                      selectedPaymentMethod={selectedMethod}
                    />
                  </Col>
                  <Col className='px-0' xl={2}></Col>
                  {/* Right: Stepper and Step Content */}
                  <Col
                    className='px-0 pt-lg-2 pt-md-4 pt-sm-4 pt-4'
                    xl={5}
                    lg={6}
                    md={8}
                    sm={10}
                    xs={12}>
                    <PlanPaymentStepper
                      currentStep={step}
                      isComplete={step === stepCount}
                      selected={selectedMethod}
                      isQuestPayment={!isWireTransfer}
                      stepCount={stepCount}
                    />
                    {step === 1 && (
                      <QuestPaymentMethods
                        onMethodSelect={setSelectedMethod}
                        onContinue={handlePaymentMethodContinue}
                        isLoading={isCreatingPaymentIntent}
                        selected={selectedMethod}
                        supportedPaymentMethods={supportedPaymentMethods}
                      />
                    )}
                    {/* Step 2: Billing Info (3-step) or Pay (2-step) */}
                    {/* Previous ACH: ACH could enter this Stripe CheckoutForm path via 2/3-step flow */}
                    {step === 2 && useThreeSteps && !isWireTransfer && (
                      <QuestUserInfoForm
                        onBack={() => setStep(1)}
                        paymentType={paymentType}
                        selectedPaymentMethod={apiPaymentMethod}
                        finalAmountToPay={finalAmountToPay || 0}
                        makePayment={(data, address) => {
                          // Update billing address
                          if (address) {
                            setBillingAddress(address)
                          }
                          // QuestUserInfoForm already called the API, just use the response
                          if (data && data.stripe && data.stripe.clientSecret) {
                            setClientSecret(data.stripe.clientSecret)
                            setStep(3)
                          }
                        }}
                        canPayRemaining={paymentSummary?.canPayRemaining}
                        eurRate={eurRate}
                        selectedMethod={selectedMethod}
                        setFundingInstructions={setFundingInstructions}
                        setStep={setStep}
                        billingAddress={billingAddress}
                        setBillingAddress={setBillingAddress}
                      />
                    )}
                    {/* Step 2: Pay (2-step flow) */}
                    {/* Previous ACH Stripe Payment Element path (commented — ACH now uses wire popup):
                    {step === 2 && !useThreeSteps && !isWireTransfer && clientSecret && (
                      <Elements ...>
                        <CheckoutForm paymentMethod="ach" ... />
                      </Elements>
                    )}
                    */}
                    {step === 2 &&
                      !useThreeSteps &&
                      !isWireTransfer &&
                      clientSecret && (
                        <>
                          {showAddressForm ? (
                            <QuestUserInfoForm
                              onBack={() => setShowAddressForm(false)}
                              paymentType={paymentType}
                              selectedPaymentMethod={apiPaymentMethod}
                              finalAmountToPay={finalAmountToPay || 0}
                              makePayment={async (data, values) => {
                                setBillingAddress(values)
                                setShowAddressForm(false)
                                // Optionally call address update API if needed
                              }}
                              canPayRemaining={paymentSummary?.canPayRemaining}
                              eurRate={eurRate}
                              billingAddress={billingAddress}
                              setBillingAddress={setBillingAddress}
                              isAddressUpdateOnly={true}
                            />
                          ) : (
                            <Elements
                              stripe={stripePromise}
                              options={{
                                clientSecret,
                                paymentMethodCreation: 'manual',
                              }}>
                              {!redirect_status && (
                                <CheckoutForm
                                  onBack={() => setStep(1)}
                                  paymentMethod={selectedMethod}
                                  billingAddress={billingAddress}
                                  showVerifyBillingInfo={true}
                                  onVerifyBillingInfo={() => {
                                    gtmContinueExperienceBillingInfoAction(
                                      questId,
                                      userId,
                                    )
                                    setShowAddressForm(true)
                                  }}
                                  onMakePayment={() => {}}
                                  clientSecret={clientSecret}
                                />
                              )}
                              {redirect_status && <QuestPaymentStatus />}
                            </Elements>
                          )}
                        </>
                      )}
                    {/* Step 3: Payment Processing (3-step flow) */}
                    {/* Previous ACH could reach Stripe CheckoutForm here after billing */}
                    {step === 3 && useThreeSteps && !isWireTransfer && (
                      <>
                        {clientSecret ? (
                          <Elements
                            stripe={stripePromise}
                            options={{
                              clientSecret,
                              paymentMethodCreation: 'manual',
                            }}>
                            {!redirect_status && (
                              <CheckoutForm
                                onBack={() => setStep(2)}
                                paymentMethod={selectedMethod}
                                billingAddress={billingAddress}
                                showVerifyBillingInfo={false}
                                onVerifyBillingInfo={() => {}}
                                onMakePayment={() => {}}
                                clientSecret={clientSecret}
                              />
                            )}
                            {redirect_status && <QuestPaymentStatus />}
                          </Elements>
                        ) : (
                          <div className='payment-form-shimmer mt-4'>
                            <div className='text-center'>
                              <div
                                className='spinner-border text-primary'
                                role='status'>
                                <span className='visually-hidden'>
                                  Loading...
                                </span>
                              </div>
                              <p className='mt-3'>Preparing payment form...</p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {/* Wire transfer + ACH: 2 steps, billing form then bank instructions popup */}
                    {isWireTransfer && step === 2 && (
                      <QuestUserInfoForm
                        onBack={() => setStep(1)}
                        paymentType={paymentType}
                        selectedPaymentMethod={apiPaymentMethod}
                        finalAmountToPay={finalAmountToPay || 0}
                        makePayment={makePayment}
                        canPayRemaining={paymentSummary?.canPayRemaining}
                        eurRate={eurRate}
                        billingAddress={billingAddress}
                        setBillingAddress={setBillingAddress}
                      />
                    )}
                  </Col>
                </>
              )}
            </Row>
          </Container>
        </section>
        {fundingInstructions && (
          <TransferInstructions
            fundingInstructions={fundingInstructions}
            onClose={() => {
              setFundingInstructions(null)
              history.replace(`/your-experiences?questId=${questId}`)
            }}
          />
        )}
      </main>
    </>
  )
}

export default QuestPayment
